import type {
    EntityPatch,
    HistoryDomainAdapter,
    HistoryEntitySnapshot,
    HistoryPatchApplyInput,
} from '..';
import {isSerializablePlainRecord} from './SerializablePlainData';

/**
 * 草图实体历史 adapter ID。
 */
export const SKETCH_HISTORY_ADAPTER_ID = 'history-domain:sketch-entity';

/**
 * 草图实体快照。
 *
 * 设计说明：
 * 1. Phase 1.1 只定义 plain data 契约，不绑定 `SketchPointObject`、`SketchLineObject` 等运行时对象；
 * 2. 约束、尺寸和 solver island 的真实记录属于 Phase 2。
 */
export interface SketchEntityHistorySnapshot {
    /** 草图 ID。 */
    readonly sketchId: string;
    /** 草图实体 ID。 */
    readonly entityId: string;
    /** 草图实体类型。 */
    readonly entityType: string;
    /** 可序列化几何数据。 */
    readonly geometry: Readonly<Record<string, unknown>>;
    /** 可序列化属性数据。 */
    readonly properties?: Readonly<Record<string, unknown>>;
}

/**
 * 草图历史运行时存储。
 */
export interface SketchHistoryStore {
    /**
     * 写入草图实体。
     *
     * @param snapshot 草图实体快照。
     */
    readonly upsertSketchEntity: (snapshot: SketchEntityHistorySnapshot) => void;
    /**
     * 删除草图实体。
     *
     * @param entityId 草图实体 ID。
     */
    readonly deleteSketchEntity: (entityId: string) => void;
}

/**
 * 判断草图快照是否为 plain data。
 *
 * @param snapshot 草图实体快照。
 * @returns 是否为 plain data。
 */
export function isPlainSketchEntitySnapshot(snapshot: SketchEntityHistorySnapshot): boolean {
    return typeof snapshot.sketchId === 'string'
        && typeof snapshot.entityId === 'string'
        && typeof snapshot.entityType === 'string'
        && isSerializablePlainRecord(snapshot.geometry)
        && (snapshot.properties === undefined || isSerializablePlainRecord(snapshot.properties));
}

/**
 * 应用草图实体快照。
 *
 * @param input 补丁应用输入。
 */
function applySketchSnapshot(
    input: HistoryPatchApplyInput<SketchEntityHistorySnapshot, SketchHistoryStore>,
): void {
    const snapshot = input.snapshot;

    if (!snapshot.exists) {
        input.context.deleteSketchEntity(input.patch.entityId);

        return;
    }

    if (!snapshot.data) {
        throw new Error(`草图实体 ${input.patch.entityId} 缺少快照数据。`);
    }

    if (!isPlainSketchEntitySnapshot(snapshot.data)) {
        throw new Error(`草图实体 ${input.patch.entityId} 的快照不是可序列化 plain data。`);
    }

    input.context.upsertSketchEntity(snapshot.data);
}

/**
 * 比较草图实体快照。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @returns 是否语义相等。
 */
function areSketchSnapshotsEqual(
    left: HistoryEntitySnapshot<SketchEntityHistorySnapshot>,
    right: HistoryEntitySnapshot<SketchEntityHistorySnapshot>,
): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * 创建草图历史 adapter。
 *
 * @returns 草图历史 adapter。
 */
export function createSketchHistoryAdapter(): HistoryDomainAdapter<SketchEntityHistorySnapshot, SketchHistoryStore> {
    return {
        adapterId: SKETCH_HISTORY_ADAPTER_ID,
        kind: 'sketch-entity',
        applySnapshot: applySketchSnapshot,
        areSnapshotsEqual: areSketchSnapshotsEqual as HistoryDomainAdapter<
            SketchEntityHistorySnapshot,
            SketchHistoryStore
        >['areSnapshotsEqual'],
    };
}

/**
 * 创建草图实体补丁。
 *
 * @param patch 补丁字段。
 * @returns 草图实体补丁。
 */
export function createSketchEntityHistoryPatch(
    patch: Omit<EntityPatch<SketchEntityHistorySnapshot>, 'adapterId' | 'entityKind'>,
): EntityPatch<SketchEntityHistorySnapshot> {
    return {
        ...patch,
        entityKind: 'sketch-entity',
        adapterId: SKETCH_HISTORY_ADAPTER_ID,
    };
}
